import { EventContextProvider } from "@codat/analytics";
import {
  BellIcon,
  ErrorDialog,
  SquareNewWindowIcon,
  ZeroState,
} from "@codat/orchard-ui";
import {
  AlertingRule,
  useGetCompaniesQuery,
  useGetRulesQuery,
} from "api/public";
import { useFeatureStatus } from "portal-ui-remote/FeatureStatusContext";
import { useUserState } from "portal-ui-remote/UserStateContext";
import React, { useState } from "react";
import { Redirect } from "react-router-dom";

import { paths } from "components/App/paths";
import Button from "components/Button";
import Page from "components/Page";
import PageHeader from "components/PageHeader";

import CreateRuleModal from "./CreateRuleModal";
import DeleteRuleModal from "./DeleteRuleModal";
import EditRuleModal from "./EditRuleModal";
import styles from "./WebhookRules.module.css";
import WebhookRulesTable from "./WebhookRulesTable";

const resultsPerPage = 20;

const WebhookRules = () => {
  const { featureStatus } = useFeatureStatus();
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [ruleToEdit, setRuleToEdit] = useState<AlertingRule | null>(null);
  const [ruleToDelete, setRuleToDelete] = useState<AlertingRule | null>(null);
  const [showCreateRuleModal, setShowCreateRuleModal] = useState(false);
  const { userState } = useUserState();
  const [{ data, error, loading }, refetchRules] = useGetRulesQuery({
    page: pageIndex,
    pageSize: resultsPerPage,
    orderBy: "type",
  });
  const [
    { data: companies, error: companiesError, loading: companiesLoading },
  ] = useGetCompaniesQuery({
    queryParams: {
      page: 1,
      pageSize: 500,
    },
  });

  const isUsingNewWebhooks = featureStatus?.["Rules.UseNewWebhooks"] ?? false;
  const userCanDelete = userState?.userPermissions.includes(
    "clients_rules_delete"
  );
  const userCanEdit = userState?.userPermissions.includes("clients_rules_edit");
  const userCanCreate =
    userState?.userPermissions.includes("clients_rules_add");

  const onDeleteRule = userCanDelete ? setRuleToDelete : undefined;

  const onEditRule = userCanEdit ? setRuleToEdit : undefined;

  const isError = error || companiesError;
  const isLoading = loading || companiesLoading;

  const renderTableOrEmptyState = () => {
    return data?.totalResults || isLoading ? (
      <WebhookRulesTable
        rules={data}
        loading={isLoading}
        companies={companies}
        onEditRule={onEditRule}
        onDeleteRule={onDeleteRule}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        pageSize={resultsPerPage}
      />
    ) : (
      <ZeroState
        className={styles.emptyState}
        icon={<BellIcon />}
        title="No webhook rules"
        description="Created webhooks will appear here"
        ctaText="Create a rule"
        onCtaClick={() => setShowCreateRuleModal(true)}
      />
    );
  };

  if (isUsingNewWebhooks) {
    return <Redirect to={paths.monitor.EVENTS} />;
  }

  return (
    <Page>
      <EventContextProvider name="Webhook rules">
        <div className={styles.header}>
          <PageHeader header="Webhook rules" />
          <div className={styles.headerButtons}>
            <Button
              data-testid="alerts-documentation"
              buttonName="Webhooks Documentation"
              eventProperties={{ page: "Documentation" }}
              href="https://docs.codat.io/using-the-api/webhooks/legacy/core-rules-create"
              color="interface"
              variant="secondary"
              target="_blank"
              aria-label="Webhooks documentation"
            >
              Webhooks documentation
              <SquareNewWindowIcon className={styles.icon} />
            </Button>
            {userCanCreate && (
              <Button
                label="Create new rule"
                buttonName="Create New Rule"
                onClick={() => setShowCreateRuleModal(true)}
              />
            )}
          </div>
        </div>
        {isError ? (
          <ErrorDialog
            statusCode={undefined}
            correlationId={
              error?.response?.data?.correlationId ??
              companiesError?.response?.data?.correlationId
            }
          />
        ) : (
          renderTableOrEmptyState()
        )}
        {showCreateRuleModal && (
          <EventContextProvider name="Create Rule Modal">
            <CreateRuleModal
              onClose={() => setShowCreateRuleModal(false)}
              onSuccess={() => {
                setShowCreateRuleModal(false);
                refetchRules();
              }}
              companies={companies}
            />
          </EventContextProvider>
        )}
        {ruleToEdit && (
          <EventContextProvider name="Edit Rule Modal">
            <EditRuleModal
              rule={ruleToEdit}
              companies={companies}
              onClose={() => setRuleToEdit(null)}
              onSuccess={() => {
                setRuleToEdit(null);
                refetchRules();
              }}
            />
          </EventContextProvider>
        )}
        {ruleToDelete && (
          <EventContextProvider name="Delete Rule Modal">
            <DeleteRuleModal
              rule={ruleToDelete}
              onClose={() => setRuleToDelete(null)}
              onSuccess={() => {
                setRuleToDelete(null);
                refetchRules();
              }}
            />
          </EventContextProvider>
        )}
      </EventContextProvider>
    </Page>
  );
};

export default WebhookRules;
